import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

interface FAQ {
  categoria: string;
  pregunta: string;
  respuesta: React.ReactNode;
}

const FAQS: FAQ[] = [
  {
    categoria: "Acceso",
    pregunta: "¿Cómo recupero mi contraseña?",
    respuesta: (
      <>
        En la pantalla de inicio de sesión presiona la opción "Recuperar contraseña" e
        ingresa el correo asociado a tu cuenta. Recibirás un enlace temporal para
        establecer una nueva contraseña. Si el correo no llega revisa la carpeta de
        spam y verifica que el correo registrado sea el correcto; en caso contrario
        contacta a Calidad o usa la sección Asistencia.
      </>
    ),
  },
  {
    categoria: "Registro Legal",
    pregunta: "¿Por qué no aparece mi empresa al crear una solicitud?",
    respuesta: (
      <>
        Las solicitudes sólo se pueden crear desde Registros Legales que estén en
        estatus <span className="font-medium">APROBADO</span> o <span className="font-medium">ACTIVO</span>
        y con el sector correspondiente <span className="font-medium">habilitado</span> por
        Calidad. Si tu RL aparece en revisión documental, observado o suspendido, no es
        elegible para nuevas solicitudes. Si el sector (Telecom, Eléctrica, etc.) no está
        habilitado, escribe a <a className="text-primary-strong hover:underline" href="mailto:calidad@npmx.org">calidad@npmx.org</a>
        {" "}para solicitarlo.
      </>
    ),
  },
  {
    categoria: "Documentos",
    pregunta: "¿Qué documentos debo cargar al expediente?",
    respuesta: (
      <>
        El checklist mínimo depende del sector. En Telecom los obligatorios son:
        Reporte de pruebas de laboratorio, Declaración de conformidad del fabricante,
        Fotografías del producto y, según aplique, Certificado de calibración,
        Manual de usuario y Etiquetado. Adicionalmente puedes cargar cualquier
        archivo extra en la categoría <span className="font-medium">Otros documentos</span>.
        El detalle del expediente te muestra el checklist en tiempo real.
      </>
    ),
  },
  {
    categoria: "Documentos",
    pregunta: "¿Qué formatos de archivo acepta el sistema?",
    respuesta: (
      <>
        Por defecto los documentos del expediente se aceptan en PDF. La categoría
        <span className="font-medium"> Otros documentos </span>
        acepta además Word (.doc / .docx), imágenes (JPG / PNG), Excel (.xls / .xlsx) y ZIP.
        Cada tipo tiene un tamaño máximo (25 MB la mayoría, 50 MB para reportes y
        manuales). Antes de aceptar el archivo se valida MIME en el servidor y se
        escanea con antivirus.
      </>
    ),
  },
  {
    categoria: "Solicitudes",
    pregunta: "¿Cómo seleccionar el esquema correcto en Telecom?",
    respuesta: (
      <>
        Al iniciar una solicitud Telecom el sistema te muestra los cuatro esquemas con
        una descripción breve para que identifiques cuál aplica a tu caso (lote único,
        múltiples modelos, configuración única, etc.). Si tienes dudas, revisa el
        tutorial de Telecom o consulta con el equipo técnico de NPMX vía la sección
        Contáctanos.
      </>
    ),
  },
  {
    categoria: "Solicitudes",
    pregunta: "¿Dónde consulto el estatus de mi solicitud?",
    respuesta: (
      <>
        En el módulo <span className="font-medium">Telecomunicaciones</span> verás la
        lista con la columna "Estado". El detalle de la solicitud muestra un stepper
        visual de las etapas (Solicitud, Facturación, Evaluación, Revisión y decisión,
        Aceptada). Además la campana del encabezado indica cuántas
        solicitudes tienes "en proceso" — al hacer clic te lleva al listado filtrado.
      </>
    ),
  },
];

export function AyudaFaqTab() {
  const [q, setQ] = useState("");
  const [abierta, setAbierta] = useState<number | null>(0);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return FAQS;
    return FAQS.filter(
      (f) =>
        f.pregunta.toLowerCase().includes(term) ||
        f.categoria.toLowerCase().includes(term),
    );
  }, [q]);

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-surface p-5 shadow-card">
        <h2 className="text-base font-semibold text-fg">Preguntas frecuentes</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Selecciona una pregunta para ver la respuesta. Usa el buscador para filtrar por
          tema o palabra clave.
        </p>
        <div className="mt-3 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar (ej. contraseña, documentos, certificado…)"
            className="w-full rounded border border-edge-strong py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border bg-surface shadow-card">
        {filtradas.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-subtle">
            Sin coincidencias. Intenta con otra palabra clave o revisa la sección
            Contáctanos para escribirnos directamente.
          </div>
        ) : (
          <ul className="divide-y">
            {filtradas.map((f, i) => {
              const expand = abierta === i;
              return (
                <li key={`${f.categoria}-${f.pregunta}`}>
                  <button
                    type="button"
                    onClick={() => setAbierta(expand ? null : i)}
                    className="flex w-full items-start justify-between gap-3 px-5 py-3 text-left transition hover:bg-surface-2"
                  >
                    <div className="min-w-0">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-fg-faint">
                        {f.categoria}
                      </div>
                      <div className="text-sm font-medium text-fg">{f.pregunta}</div>
                    </div>
                    <ChevronDown
                      className={`mt-1 h-4 w-4 shrink-0 text-fg-faint transition ${
                        expand ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expand && (
                    <div className="border-t bg-surface-2 px-5 py-3 text-sm leading-relaxed text-fg-muted">
                      {f.respuesta}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
